import React, { useState, useCallback } from 'react';
import { Eye, Image, Edit } from 'lucide-react';

const BlocksGrid = ({ blocks, installing, mode = 'admin', onInstall, onRemove, onUpdate, stats }) => {

  const isPublic = mode === 'public';
  const [confirmModal, setConfirmModal] = useState(null);
  const [updateModal, setUpdateModal] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const handleImageClick = useCallback((e, block) => {
	e.preventDefault();
	e.stopPropagation();
	if (block.preview_image) {
	  setLightboxImage({ src: block.preview_image, alt: block.name || block.slug });
	}
  }, []);

  const closeLightbox = useCallback((e) => {
	e.preventDefault();
	e.stopPropagation();
	setLightboxImage(null);
  }, []);
	
  const ImageWithFallback = ({ src, alt, className, isNav }) => {
	const [imageError, setImageError] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false);

	return (
	  <div className={`${className} or-bg-gray-100 or-flex or-items-center or-justify-center or-relative or-overflow-hidden or-rounded`}>
		{!imageError && src ? (
		  <>
			<img
			  src={src}
			  alt={alt}
			  loading="lazy"
			  className={`or-w-full or-h-full ${isNav ? 'or-object-contain' : 'or-object-cover'} or-transition-opacity or-duration-200 ${
				imageLoaded ? 'or-opacity-100' : 'or-opacity-0'
			  }`}
			  onLoad={() => setImageLoaded(true)}
			  onError={() => setImageError(true)}
			/>
			{!imageLoaded && (
			  <div className="or-absolute or-inset-0 or-flex or-items-center or-justify-center">
				<div className="or-animate-spin or-rounded-full or-h-6 or-w-6 or-border-2 or-border-blue-600 or-border-t-transparent"></div>
			  </div>
			)}
		  </>
		) : (
		  <div className="or-flex or-flex-col or-items-center or-justify-center or-text-gray-400">
			<Image size={40} />
			<span className="or-text-sm or-mt-2">No Preview</span>
		  </div>
		)}
	  </div>
	);
  };

  const handlePreview = (block) => {
	console.log('Preview block:', block);

	if (block.preview_url) {
	  window.open(block.preview_url, '_blank');
	} else {
	  alert(`Preview functionality for ${block.name || block.slug} - implement your preview logic here`);
	}
  };

  const handleEditInElementor = (block) => {
	if (block.template_id) {
	  const editUrl = `${window.location.origin}/wp-admin/post.php?post=${block.template_id}&action=elementor`;
	  window.open(editUrl, '_blank');
	} else {
	  alert('Template ID not found. Please try reinstalling this block.');
	}
  };

  const handleInstallClick = (block) => {
	if (block.has_update) {
	  // Show update options modal
	  setUpdateModal({
		blockName: block.name || block.slug,
		blockSlug: block.slug,
		updateInfo: block.update_info
	  });
	} else if (block.installed) {
	  // Show remove confirmation
	  setConfirmModal({
		blockName: block.name || block.slug,
		blockSlug: block.slug
	  });
	} else {
	  // Regular install
	  onInstall(block.slug);
	}
  };
  
  const confirmUpdate = (overwrite = false) => {
	if (updateModal) {
	  onUpdate(updateModal.blockSlug, overwrite);
	  setUpdateModal(null);
	}
  };
  
  const confirmRemove = () => {
	if (confirmModal) {
	  onRemove(confirmModal.blockSlug);
	  setConfirmModal(null);
	}
  };

  // No blocks state
  if (!blocks || blocks.length === 0) {
	return (
	  <div className="or-text-center or-py-12">
		<div className="or-text-gray-400 or-mb-4">
		  <Image size={48} className="or-mx-auto" />
		</div>
		<h3 className="or-text-lg or-font-medium or-text-gray-900 or-mb-2">No blocks found</h3>
		<p className="or-text-gray-500">Try adjusting your search or filter criteria</p>
	  </div>
	);
  }

  return (
	<div>
	  {/* Responsive Grid */}
	  <div className="or-grid or-grid-cols-1 md:or-grid-cols-2 lg:or-grid-cols-3 or-gap-6">
		{blocks.map((block) => {
		  const isInstalling = installing.has(block.slug);
		  
		  return (
			<div key={block.slug} className="or-block or-bg-white or-rounded-lg or-border or-p-4 or-shadow-sm hover:or-shadow-md or-transition-shadow or-group">
			  {/* Preview Image */}
			  <div
				className="orbit-preview-image or-w-full or-h-48 or-mb-4 or-overflow-hidden or-rounded or-cursor-pointer or-relative"
				onClick={(e) => handleImageClick(e, block)}
			  >
				<ImageWithFallback
				  src={block.preview_image || block.thumbnail || ''}
				  alt={block.name || block.slug}
				  className="or-w-full or-h-full"
				  isNav={block.categories && block.categories.includes('navigation')}
				/>
				{/* Zoom Overlay */}
				<div className="orbit-zoom-overlay or-absolute or-inset-0 or-flex or-items-center or-justify-center" style={{ pointerEvents: 'none' }}>
				  <div className="orbit-zoom-icon or-bg-white or-rounded-full or-w-12 or-h-12 or-flex or-items-center or-justify-center or-shadow-lg" style={{ opacity: 0 }}>
					<Eye size={24} className="or-text-gray-800" />
				  </div>
				</div>
			  </div>
			  
			  {/* Title and Status on same line */}
			  <div className="or-flex or-items-center or-justify-between or-mb-2">
				<h3 className="or-text-lg or-font-semibold or-text-gray-900 or-truncate or-flex-1">
				  {block.name || block.slug}
				</h3>
				{!isPublic && (
				  <div className="or-flex or-items-center or-gap-2">
					{block.has_update && (
					  <span className="or-inline-flex or-items-center or-gap-1 or-px-2 or-py-1 or-text-xs or-rounded-full or-font-medium or-flex-shrink-0" style={{backgroundColor: '#f3e8ff', color: '#7504CD'}}>
						<i className="fa-solid fa-arrows-rotate"></i>
						<span>Update Available</span>
					  </span>
					)}
					{block.installed && !block.has_update && (
					  <span className="or-inline-flex or-items-center or-px-2 or-py-1 or-bg-green-100 or-text-green-800 or-text-xs or-rounded-full or-font-medium or-flex-shrink-0">
						✓ Installed
					  </span>
					)}
				  </div>
				)}
			  </div>
			  
			  {isPublic ? (
				/* Public mode: Category and Demo button on same line */
				<div className="or-flex or-items-center or-justify-between">
				  <div>
					{block.categories && block.categories.length > 0 && (
					  <>
						<span className="or-inline-block or-px-2 or-py-1 or-bg-gray-100 or-text-gray-600 or-text-sm or-rounded">
						  {typeof block.categories[0] === 'string'
							  ? block.categories[0].charAt(0).toUpperCase() + block.categories[0].slice(1)
							  : (block.categories[0].name || block.categories[0].slug).charAt(0).toUpperCase() + (block.categories[0].name || block.categories[0].slug).slice(1)
							}
						</span>
						{block.categories.length > 1 && (
						  <span className="or-text-gray-500 or-text-sm or-ml-1">
							+{block.categories.length - 1} more
						  </span>
						)}
					  </>
					)}
				  </div>
				  <button
					onClick={() => handlePreview(block)}
					className="button button-secondary"
				  >
					<Eye size={16} className="or-inline or-mr-1" />
					Demo
				  </button>
				</div>
			  ) : (
				/* Admin mode: Category above, Install/Remove + Edit/Demo buttons below */
				<>
				  {/* Category */}
				  {block.categories && block.categories.length > 0 && (
					<div className="or-mb-3">
					  <span className="or-inline-block or-px-2 or-py-1 or-bg-gray-100 or-text-gray-600 or-text-sm or-rounded">
						{typeof block.categories[0] === 'string'
							? block.categories[0].charAt(0).toUpperCase() + block.categories[0].slice(1)
							: (block.categories[0].name || block.categories[0].slug).charAt(0).toUpperCase() + (block.categories[0].name || block.categories[0].slug).slice(1)
						  }
					  </span>
					  {block.categories.length > 1 && (
						<span className="or-text-gray-500 or-text-sm or-ml-1">
						  +{block.categories.length - 1} more
						</span>
					  )}
					</div>
				  )}

				  {/* Action Buttons */}
				  <div className="or-flex or-items-center or-justify-between">
					<button
					  onClick={() => handleInstallClick(block)}
					  disabled={isInstalling}
					  className={`button ${
						block.has_update
						  ? 'button-primary'
						  : block.installed
							? 'button-secondary'
							: 'button-primary'
					  } ${isInstalling ? 'or-opacity-50 or-cursor-not-allowed' : ''}`}
					>
					  {isInstalling ? (
						<span className="or-flex or-items-center or-gap-2">
						  <span className="or-animate-spin or-rounded-full or-h-4 or-w-4 or-border-2 or-border-current or-border-t-transparent"></span>
						  <span>
							{block.has_update ? 'Updating...' : block.installed ? 'Removing...' : 'Installing...'}
						  </span>
						</span>
					  ) : (
						<>
						  {block.has_update ? (
							<i className="fa-solid fa-arrows-rotate or-mr-2"></i>
						  ) : block.installed ? (
							<i className="fa-solid fa-trash-can or-mr-2"></i>
						  ) : (
							<i className="fa-solid fa-download or-mr-2"></i>
						  )}
						  {block.has_update ? 'Update' : block.installed ? 'Remove' : 'Install'}
						</>
					  )}
					</button>

					{block.installed && block.template_id ? (
					  <button
						onClick={() => handleEditInElementor(block)}
						className="button button-secondary or-ml-2"
					  >
						<Edit size={16} className="or-inline or-mr-1" />
						Edit
					  </button>
					) : (
					  <button
						onClick={() => handlePreview(block)}
						className="button button-secondary or-ml-2"
					  >
						<Eye size={16} className="or-inline or-mr-1" />
						Demo
					  </button>
					)}
				  </div>
				</>
			  )}
			</div>
		  );
		})}
	  </div>
	  {/* Confirmation Modal */}
	  {confirmModal && (
		<div className="or-fixed or-inset-0 or-bg-black or-bg-opacity-50 or-flex or-items-center or-justify-center or-z-50">
		  <div className="or-bg-white or-rounded-lg or-p-6 or-max-w-md or-mx-4">
			<h3 className="or-text-lg or-font-semibold or-text-gray-900 or-mb-3">
			  Remove Block
			</h3>
			
			<p className="or-text-gray-600 or-mb-2">
			  Are you sure you want to remove <strong>{confirmModal.blockName}</strong>?
			</p>
			
			<div className="or-bg-yellow-50 or-border or-border-yellow-200 or-rounded or-p-3 or-mb-4">
			  <p className="or-text-yellow-800 or-text-sm">
				⚠️ <strong>Warning:</strong> This will permanently delete the block from your Elementor library. This cannot be undone. Any pages using this block may be affected.
			  </p>
			</div>
			
			<div className="or-flex or-justify-end or-gap-3">
			  <button
				onClick={() => setConfirmModal(null)}
				className="or-px-4 or-py-2 or-bg-gray-100 or-text-gray-700 or-rounded hover:or-bg-gray-200"
			  >
				Cancel
			  </button>
			  <button
				onClick={confirmRemove}
				className="or-px-4 or-py-2 or-bg-red-600 or-text-white or-rounded hover:or-bg-red-700"
			  >
				Remove Block
			  </button>
			</div>
		  </div>
		</div>
	  )}
	  {/* Update Options Modal */}
	  {updateModal && (
		<div className="or-fixed or-inset-0 or-bg-black or-bg-opacity-50 or-flex or-items-center or-justify-center or-z-50">
		  <div className="or-bg-white or-rounded-lg or-p-6 or-max-w-md or-mx-4">
			<h3 className="or-text-lg or-font-semibold or-text-gray-900 or-mb-4">
			  Update Block
			</h3>
			
			<p className="or-text-gray-600 or-mb-4">
			  An update is available for <strong>{updateModal.blockName}</strong>. The latest version was updated {updateModal.updateInfo?.last_updated}.
			</p>
			
			<div className="or-bg-yellow-50 or-border or-border-yellow-200 or-rounded or-p-4 or-mb-4">
			  <p className="or-text-yellow-800 or-text-sm">
				⚠️ <strong>Warning:</strong> Replace existing will overwrite the block already in your library. This won't affect your pages unless you have directly imported this as a template into your pages.
			  </p>
			</div>
			
			<div className="or-flex or-justify-end or-gap-3">
			  <button
				onClick={() => setUpdateModal(null)}
				className="or-px-4 or-py-2 or-bg-gray-100 or-text-gray-700 or-rounded hover:or-bg-gray-200"
			  >
				Cancel
			  </button>
			  
			  <button
				onClick={() => confirmUpdate(true)}
				className="or-px-4 or-py-2 or-bg-primary-600 or-text-white or-rounded hover:or-bg-primary-700"
			  >
				Replace Existing
			  </button>
			</div>
		  </div>
		</div>
	  )}

	  {/* Lightbox Modal */}
	  {lightboxImage && (
		<div
		  className="or-fixed or-inset-0 or-flex or-items-center or-justify-center or-p-4"
		  style={{
			zIndex: 99999,
			backgroundColor: '#f3f4f6',
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0
		  }}
		  onClick={closeLightbox}
		>
		  <button
			onClick={closeLightbox}
			className="or-absolute or-top-4 or-right-4 or-w-12 or-h-12 or-rounded-full or-flex or-items-center or-justify-center or-text-3xl or-transition-all or-font-bold"
			style={{
			  zIndex: 100000,
			  backgroundColor: '#e5e7eb',
			  color: '#000'
			}}
		  >
			×
		  </button>
		  <div className="or-relative or-max-w-7xl or-max-h-full" onClick={(e) => e.stopPropagation()}>
			<img
			  src={lightboxImage.src}
			  alt={lightboxImage.alt}
			  className="or-max-w-full or-max-h-screen or-rounded-lg or-shadow-2xl"
			  style={{ maxHeight: '90vh' }}
			/>
			{lightboxImage.alt && (
			  <p className="or-text-gray-900 or-text-center or-mt-4 or-text-lg">{lightboxImage.alt}</p>
			)}
		  </div>
		</div>
	  )}
	</div>
  )
};

export default BlocksGrid;